"use client";

import { c as _c } from "react/compiler-runtime";
import type { NotificationResponseDto } from "@dc/gateway-jsonapi";
import { notificationsControllerMarkAsRead, useNotificationsControllerGetMyNotifications, useNotificationsControllerMarkAllAsRead } from "@dc/gateway-jsonapi";
import { useDebounce } from "@dc/ui/hooks/useDebounce";
import { useMutation } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { NotificationGroup } from "@/hooks/useNotifications/useNotifications.interface";
import { formatUnreadCount } from "./formatUnreadCount";
import { useUnreadNotificationCount } from "./hooks/useUnreadNotificationCount";
import type { NotificationsContextType, NotificationsProviderProps } from "./NotificationsProvider.interface";
export const NotificationsContext = createContext<NotificationsContextType | null>(null);
type JsonApiNotificationItem = {
  id: string;
  type: string;
  attributes: NotificationResponseDto;
  relationships: {
    user: {
      data: {
        type: string;
        id: string;
      };
    };
  };
};
const parseSqlTimestamp = (timestamp: string): Date => {
  return new Date(timestamp.replace(" ", "T"));
};

/**
 * @param notifications - The notifications to group by date.
 * @returns The notifications grouped by date.
 */
const groupNotificationsByDate = (notifications: NotificationResponseDto[]): NotificationGroup[] => {
  const groups: Record<string, NotificationResponseDto[]> = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  for (const notification of notifications) {
    const {
      createdAt
    } = notification;
    const notificationDate = parseSqlTimestamp(createdAt);
    const notificationDateOnly = new Date(notificationDate.getFullYear(), notificationDate.getMonth(), notificationDate.getDate());
    let dateLabel: string;
    if (notificationDateOnly.getTime() === today.getTime()) {
      dateLabel = "TODAY";
    } else if (notificationDateOnly.getTime() === yesterday.getTime()) {
      dateLabel = "YESTERDAY";
    } else {
      dateLabel = [notificationDateOnly.getFullYear(), String(notificationDateOnly.getMonth() + 1).padStart(2, "0"), String(notificationDateOnly.getDate()).padStart(2, "0")].join("-");
    }
    if (!groups[dateLabel]) {
      groups[dateLabel] = [];
    }
    groups[dateLabel].push(notification);
  }
  const dateGroups: Array<{
    label: string;
    date: Date;
  }> = [];
  const sortedGroups: Array<{
    label: string;
    notifications: NotificationResponseDto[];
  }> = [];
  for (const [label, notifs] of Object.entries(groups)) {
    if (label === "TODAY" || label === "YESTERDAY") {
      sortedGroups.push({
        label,
        notifications: notifs
      });
    } else {
      const firstNotification = notifs[0];
      if (firstNotification) {
        const date = parseSqlTimestamp(firstNotification.createdAt);
        dateGroups.push({
          label,
          date
        });
      }
    }
  }
  dateGroups.sort((a, b) => b.date.getTime() - a.date.getTime());
  const result: NotificationGroup[] = [];
  for (const {
    label,
    notifications: notifs
  } of sortedGroups) {
    result.push({
      date: label,
      label,
      notifications: notifs
    });
  }
  for (const {
    label
  } of dateGroups) {
    result.push({
      date: label,
      label,
      notifications: groups[label]!
    });
  }
  return result;
};

/**
 * NotificationsProvider component provides the notifications context to the app.
 * @param children - The children components to render.
 * @returns The notifications provider.
 */
export function NotificationsProvider(t0) {
  const $ = _c(39);
  const {
    children
  } = t0;
  const {
    data: notificationsData,
    refetch
  } = useNotificationsControllerGetMyNotifications();
  const {
    unreadCount,
    decrementUnreadCount,
    clearUnreadCount,
    refetchUnreadCount
  } = useUnreadNotificationCount();
  const {
    mutate: markAllAsReadMutation
  } = useNotificationsControllerMarkAllAsRead();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      meta: {
        skipInvalidation: true
      },
      mutationFn: _temp,
      onError: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    mutate: markAsReadMutation
  } = useMutation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [notifications, setNotifications] = useState(t2);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [batchIds, setBatchIds] = useState(t3);
  const debouncedBatchIds = useDebounce(batchIds, 1000);
  let t4;
  let t5;
  if ($[3] !== notificationsData) {
    t4 = () => {
      if (notificationsData?.data && Array.isArray(notificationsData.data)) {
        const mappedNotifications = (notificationsData.data as JsonApiNotificationItem[]).map(_temp3);
        setNotifications(mappedNotifications);
      }
    };
    t5 = [notificationsData];
    $[3] = notificationsData;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== debouncedBatchIds || $[7] !== markAsReadMutation) {
    t6 = () => {
      if (debouncedBatchIds.length > 0) {
        markAsReadMutation(debouncedBatchIds);
        setBatchIds([]);
      }
    };
    t7 = [debouncedBatchIds, markAsReadMutation];
    $[6] = debouncedBatchIds;
    $[7] = markAsReadMutation;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== notifications) {
    t8 = groupNotificationsByDate(notifications);
    $[10] = notifications;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const notificationGroups = t8;
  let t9;
  if ($[12] !== decrementUnreadCount || $[13] !== notifications) {
    t9 = id => {
      const notification = notifications.find(n => n.id === id);
      if (notification && !notification.read) {
        setNotifications(prev => prev.map(n_0 => n_0.id === id ? {
          ...n_0,
          read: true
        } : n_0));
        setBatchIds(prev_0 => prev_0.includes(id) ? prev_0 : [...prev_0, id]);
        decrementUnreadCount(id);
      }
    };
    $[12] = decrementUnreadCount;
    $[13] = notifications;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const markAsRead = t9;
  let t10;
  if ($[15] !== clearUnreadCount || $[16] !== markAllAsReadMutation) {
    t10 = () => {
      setNotifications(_temp5);
      clearUnreadCount();
      markAllAsReadMutation();
    };
    $[15] = clearUnreadCount;
    $[16] = markAllAsReadMutation;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const markAllAsRead = t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setIsDrawerOpen(true);
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const openDrawer = t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsDrawerOpen(false);
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const closeDrawer = t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsDrawerOpen(_temp6);
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const toggleDrawer = t13;
  let t14;
  if ($[21] !== refetch || $[22] !== refetchUnreadCount) {
    t14 = () => {
      refetch();
      refetchUnreadCount();
    };
    $[21] = refetch;
    $[22] = refetchUnreadCount;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const refetchNotifications = t14;
  let t15;
  if ($[24] !== unreadCount) {
    t15 = formatUnreadCount(unreadCount);
    $[24] = unreadCount;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isDrawerOpen || $[27] !== isLoading || $[28] !== markAllAsRead || $[29] !== markAsRead || $[30] !== notificationGroups || $[31] !== notifications || $[32] !== refetchNotifications || $[33] !== t15 || $[34] !== unreadCount) {
    t16 = {
      notifications,
      notificationGroups,
      unreadCount,
      unreadCountLabel: t15,
      isOpen: isDrawerOpen,
      isLoading,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      markAsRead,
      markAllAsRead,
      refetchNotifications
    };
    $[26] = isDrawerOpen;
    $[27] = isLoading;
    $[28] = markAllAsRead;
    $[29] = markAsRead;
    $[30] = notificationGroups;
    $[31] = notifications;
    $[32] = refetchNotifications;
    $[33] = t15;
    $[34] = unreadCount;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const value = t16;
  let t17;
  if ($[36] !== children || $[37] !== value) {
    t17 = <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
    $[36] = children;
    $[37] = value;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}
function _temp6(prev_2) {
  return !prev_2;
}
function _temp5(prev_1) {
  return prev_1.map(_temp4);
}
function _temp4(n_1) {
  return {
    ...n_1,
    read: true
  };
}
function _temp3(item) {
  return {
    id: item.id,
    type: item.attributes.type,
    title: item.attributes.title,
    message: item.attributes.message,
    read: item.attributes.read,
    notificationContext: item.attributes.notificationContext,
    deepLink: item.attributes.deepLink,
    createdAt: item.attributes.createdAt,
    updatedAt: item.attributes.updatedAt,
    deletedAt: item.attributes.deletedAt,
    userId: item.relationships.user.data.id
  };
}
function _temp2(error) {
  console.error("Failed to mark notifications as read:", error);
}
function _temp(notificationIds) {
  return notificationsControllerMarkAsRead({
    data: {
      notificationIds
    }
  });
}